import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'loading'
	| 'modalHeaderTitle'
	| 'welcomeBannerTitle'
	| 'createAgentError'
	| 'backlogPrioritizerTitle'
	| 'backlogPrioritizerPrompt'
	| 'capacityWorkloadBalancerTitle'
	| 'capacityWorkloadBalancerPrompt'
	| 'compliancePolicyAssistantTitle'
	| 'compliancePolicyAssistantPrompt'
	| 'duplicateWorkDetectorTitle'
	| 'duplicateWorkDetectorPrompt'
	| 'escalationHandoffTitle'
	| 'escalationHandoffPrompt'
	| 'globalTranslatorTitle'
	| 'globalTranslatorPrompt'
	| 'knowledgeCuratorTitle'
	| 'knowledgeCuratorPrompt'
	| 'meetingToActionTitle'
	| 'meetingToActionPrompt'
	| 'newHireOnboardingTitle'
	| 'newHireOnboardingPrompt'
	| 'projectKickoffPackGeneratorTitle'
	| 'projectKickoffPackGeneratorPrompt'
	| 'qaHelpdeskAssistantTitle'
	| 'qaHelpdeskAssistantPrompt'
	| 'riskBlockerTrackerTitle'
	| 'riskBlockerTrackerPrompt'
	| 'selfPerformanceAssistantTitle'
	| 'selfPerformanceAssistantPrompt'
	| 'sprintRetroFacilitatorTitle'
	| 'sprintRetroFacilitatorPrompt'
	| 'surveyFeedbackSynthesiserTitle'
	| 'surveyFeedbackSynthesiserPrompt';

const messages: Record<MessageKeys, { id: string; defaultMessage: string; description?: string }> =
	defineMessages({
		loading: {
			id: 'rovo-agent-create-modal.loading',
			defaultMessage: 'Loading',
			description: 'Accessible label for the spinner while the Rovo agent create modal loads',
		},
		modalHeaderTitle: {
			id: 'rovo-agent-create-modal.modal-header-title',
			defaultMessage: 'Create agent',
			description: 'Title shown in the header of the Rovo agent create modal',
		},
		welcomeBannerTitle: {
			id: 'rovo-agent-create-modal.welcome-banner-title',
			defaultMessage: 'What should your agent do?',
			description:
				'Heading shown in the welcome banner on the landing view of the Rovo agent create modal',
		},
		createAgentError: {
			id: 'rovo-agent-create-modal.create-agent-error',
			defaultMessage: 'Something went wrong creating your agent. Please try again.',
			description:
				'Error flag shown when creating a blank draft agent from the "Start from blank" action fails',
		},
		backlogPrioritizerTitle: {
			id: 'rovo-agent-create-modal.prompt-card.backlog-prioritizer.title',
			defaultMessage: 'Backlog prioritizer',
			description: 'Title of the backlog prioritizer prompt starter card in the Create Agent modal',
		},
		backlogPrioritizerPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.backlog-prioritizer.prompt',
			defaultMessage:
				'Prioritise my backlog and rank what to do next with reasons, flags items at risk, and break oversized items into smaller pieces.',
			description: 'Prompt text for the backlog prioritizer starter card in the Create Agent modal',
		},
		capacityWorkloadBalancerTitle: {
			id: 'rovo-agent-create-modal.prompt-card.capacity-workload-balancer.title',
			defaultMessage: 'Capacity & workload balancer',
			description:
				'Title of the capacity and workload balancer prompt starter card in the Create Agent modal',
		},
		capacityWorkloadBalancerPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.capacity-workload-balancer.prompt',
			defaultMessage:
				'Balance capacity & workload by giving a clear read on who is over or under loaded across the team, spot concentration risk, and propose a fairer rebalance of work — applying reassignments on request.',
			description:
				'Prompt text for the capacity and workload balancer starter card in the Create Agent modal',
		},
		compliancePolicyAssistantTitle: {
			id: 'rovo-agent-create-modal.prompt-card.compliance-policy-assistant.title',
			defaultMessage: 'Compliance & Policy Assistant',
			description:
				'Title of the compliance and policy assistant prompt starter card in the Create Agent modal',
		},
		compliancePolicyAssistantPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.compliance-policy-assistant.prompt',
			defaultMessage:
				'Answer policy and compliance questions from my authoritative documents, and check a piece of work, request or change against the relevant rules — always citing the exact clause.',
			description:
				'Prompt text for the compliance and policy assistant starter card in the Create Agent modal',
		},
		duplicateWorkDetectorTitle: {
			id: 'rovo-agent-create-modal.prompt-card.duplicate-work-detector.title',
			defaultMessage: 'Duplicate work detector',
			description:
				'Title of the duplicate work detector prompt starter card in the Create Agent modal',
		},
		duplicateWorkDetectorPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.duplicate-work-detector.prompt',
			defaultMessage:
				"Search for duplicate or closely related work across Jira and Confluence before new work is created — so a new epic, work item or page doesn't reinvent something already started or finished. Add links to the closest matches.",
			description:
				'Prompt text for the duplicate work detector starter card in the Create Agent modal',
		},
		escalationHandoffTitle: {
			id: 'rovo-agent-create-modal.prompt-card.escalation-handoff.title',
			defaultMessage: 'Escalation Handoff',
			description: 'Title of the escalation handoff prompt starter card in the Create Agent modal',
		},
		escalationHandoffPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.escalation-handoff.prompt',
			defaultMessage:
				'Write a clear escalation handoff from the full ticket history so the specialist team has the context it needs without re-asking the customer.',
			description: 'Prompt text for the escalation handoff starter card in the Create Agent modal',
		},
		globalTranslatorTitle: {
			id: 'rovo-agent-create-modal.prompt-card.global-translator.title',
			defaultMessage: 'Global Translator',
			description: 'Title of the global translator prompt starter card in the Create Agent modal',
		},
		globalTranslatorPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.global-translator.prompt',
			defaultMessage:
				'Help with localization for global teams by translating work faithfully, maintaining a shared glossary, and keeping translated pages in sync with their source.',
			description: 'Prompt text for the global translator starter card in the Create Agent modal',
		},
		knowledgeCuratorTitle: {
			id: 'rovo-agent-create-modal.prompt-card.knowledge-curator.title',
			defaultMessage: 'Knowledge Curator',
			description: 'Title of the knowledge curator prompt starter card in the Create Agent modal',
		},
		knowledgeCuratorPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.knowledge-curator.prompt',
			defaultMessage:
				'Turn resolved tickets into a healthier knowledge base by finding gaps, drafting missing articles for approval, and keep existing pages fresh and findable.',
			description: 'Prompt text for the knowledge curator starter card in the Create Agent modal',
		},
		meetingToActionTitle: {
			id: 'rovo-agent-create-modal.prompt-card.meeting-to-action.title',
			defaultMessage: 'Meeting-to-Action Agent',
			description:
				'Title of the meeting-to-action agent prompt starter card in the Create Agent modal',
		},
		meetingToActionPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.meeting-to-action.prompt',
			defaultMessage:
				'Turn a meeting transcript or notes into structured outcomes — decisions, action items as Jira tasks with owners and due dates, a Confluence summary, and (on request) follow-up meeting invites.',
			description:
				'Prompt text for the meeting-to-action agent starter card in the Create Agent modal',
		},
		newHireOnboardingTitle: {
			id: 'rovo-agent-create-modal.prompt-card.new-hire-onboarding.title',
			defaultMessage: 'New Hire Onboarding',
			description: 'Title of the new hire onboarding prompt starter card in the Create Agent modal',
		},
		newHireOnboardingPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.new-hire-onboarding.prompt',
			defaultMessage:
				'A companion for someone\'s first weeks — answers "how do we do X here?" from your team\'s docs, walks them through their onboarding checklist, tracks completion in Jira, and points to the right people.',
			description: 'Prompt text for the new hire onboarding starter card in the Create Agent modal',
		},
		projectKickoffPackGeneratorTitle: {
			id: 'rovo-agent-create-modal.prompt-card.project-kickoff-pack-generator.title',
			defaultMessage: 'Project Kickoff Pack Generator',
			description:
				'Title of the project kickoff pack generator prompt starter card in the Create Agent modal',
		},
		projectKickoffPackGeneratorPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.project-kickoff-pack-generator.prompt',
			defaultMessage:
				"Spins up a new project from a short brief — a Confluence home page and charter, starter Jira epics, and an Atlas goal — all linked together and consistent with your team's template.",
			description:
				'Prompt text for the project kickoff pack generator starter card in the Create Agent modal',
		},
		qaHelpdeskAssistantTitle: {
			id: 'rovo-agent-create-modal.prompt-card.qa-helpdesk-assistant.title',
			defaultMessage: 'Q&A Helpdesk Assistant',
			description:
				'Title of the Q&A helpdesk assistant prompt starter card in the Create Agent modal',
		},
		qaHelpdeskAssistantPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.qa-helpdesk-assistant.prompt',
			defaultMessage:
				'A self-serve expert for your team that answers questions with citations, closes genuine documentation gaps, and reports on knowledge health over time.',
			description:
				'Prompt text for the Q&A helpdesk assistant starter card in the Create Agent modal',
		},
		riskBlockerTrackerTitle: {
			id: 'rovo-agent-create-modal.prompt-card.risk-blocker-tracker.title',
			defaultMessage: 'Risk & Blocker Tracker',
			description:
				'Title of the risk and blocker tracker prompt starter card in the Create Agent modal',
		},
		riskBlockerTrackerPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.risk-blocker-tracker.prompt',
			defaultMessage:
				'Continuously surfaces delivery risks and blockers, assesses what they threaten, maintains a living risk log, and nudges owners only with approval.',
			description:
				'Prompt text for the risk and blocker tracker starter card in the Create Agent modal',
		},
		selfPerformanceAssistantTitle: {
			id: 'rovo-agent-create-modal.prompt-card.self-performance-assistant.title',
			defaultMessage: 'Self-Performance Assistant',
			description:
				'Title of the self-performance assistant prompt starter card in the Create Agent modal',
		},
		selfPerformanceAssistantPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.self-performance-assistant.prompt',
			defaultMessage:
				'A private career companion that continuously assembles evidence of your impact from your own work across Jira, Confluence, PRs, goals and Loom — then helps you draft self-reviews, brag docs, promotion cases and weekly updates, mapped to your competency framework.',
			description:
				'Prompt text for the self-performance assistant starter card in the Create Agent modal',
		},
		sprintRetroFacilitatorTitle: {
			id: 'rovo-agent-create-modal.prompt-card.sprint-retro-facilitator.title',
			defaultMessage: 'Sprint Retro Facilitator',
			description:
				'Title of the sprint retro facilitator prompt starter card in the Create Agent modal',
		},
		sprintRetroFacilitatorPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.sprint-retro-facilitator.prompt',
			defaultMessage:
				'Auto-generates a data-backed, blameless retrospective when a sprint or epic closes — what shipped, what slipped, cycle-time outliers, recurring blockers — and creates a Confluence retro page plus follow-up actions.',
			description:
				'Prompt text for the sprint retro facilitator starter card in the Create Agent modal',
		},
		surveyFeedbackSynthesiserTitle: {
			id: 'rovo-agent-create-modal.prompt-card.survey-feedback-synthesiser.title',
			defaultMessage: 'Survey & Feedback Synthesiser',
			description:
				'Title of the survey and feedback synthesiser prompt starter card in the Create Agent modal',
		},
		surveyFeedbackSynthesiserPrompt: {
			id: 'rovo-agent-create-modal.prompt-card.survey-feedback-synthesiser.prompt',
			defaultMessage:
				'Turn raw feedback — survey responses, support tickets, interview notes, comments — into themes with evidence, sentiment, and a prioritised set of recommended actions linked back to Jira.',
			description:
				'Prompt text for the survey and feedback synthesiser starter card in the Create Agent modal',
		},
	});

export default messages;
